import React from "react";
import { useNavigate } from "react-router-dom";
import { Landmark, ShieldCheck, Car, Target, Zap, Lock, Headset, ThumbsUp, Smartphone } from "lucide-react";
import Gauge from "@/components/Gauge";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/34467e70-7230-427a-9b62-9be3c11e38b7/images/5cfab854684e51ce8c4f38ff60ec144732d278a20e6db6785b4fada82703ba07.jpeg";
const LOGO = "/assets/logo.png";

const RECIBIRAS = [
  { icon: Landmark, t: "Comparación de instituciones", d: "Te mostramos múltiples opciones y eliges la que más te conviene." },
  { icon: ShieldCheck, t: "Probabilidad de aprobación", d: "Conoce tus posibilidades reales antes de aplicar a un crédito." },
  { icon: Car, t: "Mensualidad estimada", d: "Rango de mensualidades según el vehículo y el plan que elijas." },
  { icon: Target, t: "Recomendación inteligente", d: "Nuestro algoritmo elige por ti la opción ideal para tu perfil." },
];

const TRUST = [
  { icon: ShieldCheck, t: "Tus datos están protegidos", d: "con encriptación de nivel bancario." },
  { icon: Headset, t: "Asesores disponibles", d: "para acompañarte." },
  { icon: ThumbsUp, t: "Sin impactar tu Buró", d: "hasta que tú decidas aplicar." },
  { icon: Smartphone, t: "100% en línea", d: "desde donde estés." },
];

export default function Hero() {
  const navigate = useNavigate();
  return (
    <section id="inicio" className="relative" style={{ background: "var(--af-black)" }}>
      {/* ---- Hero image band (desktop / landscape) ---- */}
      <div
        className="hero-band-default relative w-full overflow-hidden hero-glow"
        style={{ paddingTop: 72 }}
      >
        <div
          className="relative"
          style={{
            minHeight: "clamp(420px, 56vh, 620px)",
            backgroundImage: `linear-gradient(90deg, rgba(13,13,13,0.92) 0%, rgba(13,13,13,0.35) 40%, rgba(13,13,13,0.15) 65%, rgba(13,13,13,0.55) 100%), url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center 40%",
          }}
        >
          <div className="af-container relative h-full">
            {/* Left heading + gauge */}
            <div className="hero-heading absolute left-0 md:left-4 top-6 md:top-10 text-left" style={{ marginRight: "auto", maxWidth: 660 }}>
              <h1 className="h-impact text-white" style={{ fontSize: "clamp(1.4rem, 2.9vw, 2.5rem)" }}>
                EL CAMINO MÁS RÁPIDO Y SEGURO<br />PARA OBTENER <span className="text-orange-core">TU AUTO</span>
              </h1>
              <div className="flex justify-start mt-4 md:mt-6">
                <div data-testid="hero-gauge-link" className="hidden sm:block" role="link" tabIndex={0} style={{ cursor: "pointer" }} onClick={() => navigate("/registro")} onKeyDown={(e) => { if (e.key === "Enter") navigate("/registro"); }}><Gauge size={210} /></div>
              </div>
              <div data-testid="hero-al-minuto-link" className="h-impact text-white mt-2 md:mt-4" style={{ fontSize: "clamp(1.4rem, 2.6vw, 2.2rem)", cursor: "pointer" }} role="link" tabIndex={0} onClick={() => navigate("/registro")} onKeyDown={(e) => { if (e.key === "Enter") navigate("/registro"); }}>
                HAZLO <span className="text-orange-core">AL MINUTO</span>
              </div>
            </div>

            {/* Right brand lockup */}
            <div className="hero-brand absolute right-0 bottom-6 md:top-1/2 md:-translate-y-1/2 flex flex-col items-end">
              <img src={LOGO} alt="Autofinanza" className="h-32 md:h-48 w-auto -mr-3 md:-mr-5" draggable={false}
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
              <div className="font-display uppercase text-white/95 tracking-wider text-sm md:text-lg -mt-8 md:-mt-12 text-right">
                Inteligente. Rápido. <span className="text-orange-core">Para ti.</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Portrait-only hero: adaptación visual al Mockup Mobile Oficial (reutiliza elementos existentes). Oculto en desktop/landscape ---- */}
      <div className="hero-portrait hero-glow" style={{ paddingTop: 72 }}>
        <h1 className="hero-portrait-title h-impact text-white text-center">
          EL CAMINO MÁS RÁPIDO Y SEGURO<br />PARA OBTENER <span className="text-orange-core">TU AUTO</span>
        </h1>
        <div
          className="hero-portrait-media"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(13,13,13,0.55) 0%, rgba(13,13,13,0.02) 26%, rgba(13,13,13,0.10) 62%, rgba(13,13,13,0.92) 100%), url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "62% 42%",
          }}
        >
          <img
            src={LOGO}
            alt="Autofinanza"
            className="hero-portrait-logo"
            draggable={false}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
        <div data-testid="hero-portrait-tacho-link" className="hero-portrait-tacho" role="link" tabIndex={0} style={{ cursor: "pointer" }} onClick={() => navigate("/registro")} onKeyDown={(e) => { if (e.key === "Enter") navigate("/registro"); }}>
          <img
            src="/autofinanza_tacometro_vertical_mobile_oficial.png"
            alt=""
          />
          <div className="hero-portrait-tacho-text h-impact text-white">
            OBTEN UNA RESPUESTA<br /><span className="text-orange-core">AL MINUTO</span>
          </div>
        </div>
      </div>

      {/* ---- Below: two columns ---- */}
      <div className="af-container py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          {/* Left column */}
          <div className="reveal in">
            <div className="af-pill mb-6"><span className="dot" /> La plataforma inteligente que encuentra la mejor opción para ti.</div>
            <h2 className="h-impact text-white" style={{ fontSize: "clamp(2.4rem, 5vw, 3.7rem)" }}>
              TU PERFIL. TU VEHÍCULO.<br /> LA <span className="text-orange-core">MEJOR</span> OPCIÓN.
            </h2>
            <p className="text-white/80 mt-5 text-base md:text-lg max-w-xl leading-relaxed">
              Analizamos tu perfil económico y crediticio para mostrarte únicamente las alternativas donde tienes probabilidad real de ser aceptado.
            </p>
            <button
              data-testid="hero-cta-precalificacion"
              onClick={() => navigate("/registro")}
              className="af-btn-primary mt-8 w-full sm:w-auto"
              style={{ padding: "1.1rem 2rem", fontSize: "1.15rem" }}
            >
              Iniciar precalificación gratuito <span aria-hidden>→</span>
            </button>
            <div className="flex items-center gap-2 mt-4 text-white/80">
              <Lock size={18} className="text-af-orange-core" /> Seguro, rápido y sin compromiso
            </div>
          </div>

          {/* Right column: LO QUE RECIBIRÁS */}
          <div className="af-card p-6 md:p-8 reveal in">
            <div className="text-center">
              <h3 className="h-impact text-white text-2xl md:text-3xl">LO QUE RECIBIRÁS</h3>
              <p className="text-white/70 mt-1">Resultados claros, opciones reales.</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mt-6">
              {RECIBIRAS.map((r, i) => (
                <div key={i} data-testid={`recibiras-${i}`} className="rounded-xl p-4 text-center"
                  style={{ background: "#111", border: "1px solid var(--af-border-dark)" }}>
                  <r.icon size={38} className="mx-auto text-af-orange-core" strokeWidth={1.6} />
                  <div className="text-white font-semibold text-sm mt-3 leading-tight">{r.t}</div>
                  <div className="text-white/60 text-xs mt-2 leading-snug">{r.d}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-xl p-5 flex items-center gap-4"
              style={{ background: "#111", border: "1px solid var(--af-border-dark)" }}>
              <Zap size={44} className="text-af-orange-core shrink-0" />
              <div>
                <div className="h-impact text-white text-xl md:text-2xl">OBTÉN TODO ESTO <span className="text-orange-core">AL MINUTO</span></div>
                <div className="text-white/70 text-sm md:text-base">100% en línea, seguro y sin compromiso.</div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-12 pt-8" style={{ borderTop: "1px solid var(--af-border-dark)" }}>
          {TRUST.map((t, i) => (
            <div key={i} className="flex items-start gap-3">
              <t.icon size={30} className="text-af-orange-core shrink-0" strokeWidth={1.6} />
              <div className="text-sm">
                <div className="text-white font-semibold">{t.t}</div>
                <div className="text-white/60">{t.d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
